/**
 * useSeedPayRunActivity — Beta sample data for /finance/pay-runs.
 *
 * The Pay Run cockpit reads from canonical sources (jobs, contractors,
 * wraps, holdbacks, callbacks, field-adds, labor actuals, pay run records),
 * but several of those slices start empty in the in-memory beta — so a
 * fresh visitor sees blank "Ready", "Approvals", "Variance", and "Paid"
 * tabs even though the Held tab is wired correctly. That makes the page
 * look orphaned from the rest of the system.
 *
 * This hook seeds *only* the demo slices the cockpit needs to feel
 * connected to the canonical job graph:
 *   • 2 pending field-add submissions  → Approvals pivot + Held adds
 *   • 1 approved + 1 rejected field-add → audit history
 *   • 1 labor-actual record            → Variance signal vs. JobSpec estimate
 *   • 1 historical pay run record      → Paid pivot
 *
 * Idempotent via sessionStorage; never mutates jobs or contractors.
 */
import { useEffect } from "react";
import { useAppData } from "@/data/AppDataContext";
import type { FieldAddSubmission } from "./fieldAdds";
import type { LaborActualRecord } from "./laborActuals";
import type { PayRunRecord } from "./recordPayRun";
import type { PayRunView } from "./getJobPayRun";

const SENTINEL = "koredor::seed::pay-run-activity::v1";
const isDev = import.meta.env.DEV;

export function useSeedPayRunActivity() {
  const {
    jobs,
    payRunRecords, setPayRunRecords,
    fieldAddSubmissions, setFieldAddSubmissions,
    laborActualRecords, setLaborActualRecords,
  } = useAppData();

  useEffect(() => {
    if (!isDev) return;
    if (typeof window === "undefined") return;
    // AppDataContext state is in-memory (resets on every reload), so we
    // re-seed on each mount. Idempotency is enforced per-record by id below.


    // Require the sample jobs the cockpit already surfaces in "Held".
    const robert = jobs.find((j) => j.id === "JOB-004");
    const karen = jobs.find((j) => j.id === "JOB-009");
    const jennifer = jobs.find((j) => j.id === "JOB-018");
    const veronica = jobs.find((j) => j.id === "JOB-020");
    if (!robert || !karen || !jennifer || !veronica) return;

    const now = new Date();
    const iso = (daysAgo: number) =>
      new Date(now.getTime() - daysAgo * 86_400_000).toISOString();

    // ── Field-add submissions ────────────────────────────────────────
    const seedFieldAdds: FieldAddSubmission[] = ([
      {
        id: "FA-SEED-001", jobId: karen.id, partyId: "CON-001",
        amount: 285, reason: "Extra valve replacement — corroded behind wall",
        status: "pending" as const, createdAt: iso(2), createdBy: "Field · Mike R.",
        supersedesId: null,
      },
      {
        id: "FA-SEED-002", jobId: jennifer.id, partyId: "CON-001",
        amount: 1450, reason: "Tile substrate full replacement — water damage discovered",
        status: "pending" as const, createdAt: iso(1), createdBy: "Field · Mike R.",
        supersedesId: null,
      },
      {
        id: "FA-SEED-003", jobId: robert.id, partyId: "CON-001",
        amount: 180, reason: "Caulk + grout sealer (customer request)",
        status: "approved" as const, createdAt: iso(5), createdBy: "Field · Mike R.",
        decidedAt: iso(4), decidedBy: "Ops Manager",
        supersedesId: null,
      },
      {
        id: "FA-SEED-004", jobId: veronica.id, partyId: "CON-001",
        amount: 2200, reason: "Custom glass panel re-fab",
        status: "rejected" as const, createdAt: iso(6), createdBy: "Field · Mike R.",
        decidedAt: iso(3), decidedBy: "Finance",
        supersedesId: null,
      },
    ] satisfies FieldAddSubmission[]).filter((s) => !fieldAddSubmissions.some((existing) => existing.id === s.id));

    if (seedFieldAdds.length > 0) {
      setFieldAddSubmissions((prev) => [...prev, ...seedFieldAdds]);
    }

    // ── Labor actual (variance vs. JobSpec estimate) ────────────────
    if (!laborActualRecords.some((r) => r.id === "LA-SEED-001")) {
      const laborSeed: LaborActualRecord = {
        id: "LA-SEED-001", jobId: robert.id,
        lines: [
          { role: "crew_lead", hours: 11, loadedRate: 78, dollars: 858 },
          { role: "helper", hours: 9, loadedRate: 52, dollars: 468 },
        ],
        actualDollars: 1326,
        enteredBy: "Crew Lead · Team Bravo",
        enteredAt: iso(3),
        source: "actual_manual",
        note: "Glass alignment rework added ~2hrs",
      };
      setLaborActualRecords((prev) => [...prev, laborSeed]);
    }

    // ── Historical pay run record (Paid pivot) ──────────────────────
    if (!payRunRecords.some((r) => r.id === "PR-SEED-001")) {
      const paidView: PayRunView = {
        jobId: "JOB-014", partyKind: "external_contractor",
        partyId: "CON-001", partyName: "SunCoast Bath Pros",
        baseAmount: 1850,
        fieldAdds: [], fieldAddTotal: 0, fieldAddApprovedTotal: 0,
        fieldAddPendingTotal: 0, fieldAddCap: 500,
        requiresAccountingApproval: false,
        callbackDeductions: [], callbackDeductionTotal: 0,
        holdbackHeld: 0,
        labor: { estimated: null, actual: null, variance: null, variancePct: null, source: null },
        ledgerCategory: "subcontractor_payout",
        releasable: true, holdReasons: [],
        netReleasable: 1850,
      };
      const paidRecord: PayRunRecord = {
        id: "PR-SEED-001", jobId: "JOB-014",
        partyId: "CON-001", partyKind: "external_contractor",
        view: paidView,
        ledgerLines: [
          { category: "subcontractor_payout", amountCents: 185_000,
            memo: "Base payout — JOB-014", referenceId: "base" },
        ],
        netReleased: 1850,
        releasedBy: "Finance · Mike Reynolds",
        releasedAt: iso(7),
      };
      setPayRunRecords((prev) => [...prev, paidRecord]);
    }

    void SENTINEL;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);
}
